import type {
	TestShackReportInspection,
	TestShackReportItem,
	TestShackReportLegDelta,
} from "@/pages/products/test-shack/models/TestShackReport";
import type {
	TestShackReportCell,
	TestShackReportColumn,
	TestShackReportSectionRev,
	TestShackReportSectionRevRow,
} from "../models/TestShackReportSectionRev";
import { hasInspectionTolerances } from "@/pages/products/test-shack/utils/hasInspectionTolerances";

const dimensionColumnKey = (dimensionId: number) => `dimension:${dimensionId}`;

const deltaColumnKey = (deltaCode: string) => `delta:${deltaCode}`;

const toDimensionColumn = (
	dimension: TestShackReportItem,
): TestShackReportColumn => ({
	key: dimensionColumnKey(dimension.ProfileItemId),
	code: dimension.Code,
	name: dimension.Name,
	ltl: dimension.Ltl,
	nominal: dimension.Nominal,
	utl: dimension.Utl,
	hasSpec: dimension.Ltl !== null && dimension.Utl !== null,
});

const toDeltaColumn = (
	delta: TestShackReportLegDelta,
): TestShackReportColumn => ({
	key: deltaColumnKey(delta.DeltaCode),
	code: delta.DeltaCode,
	name: delta.DeltaCode,
	ltl: null,
	nominal: null,
	utl: delta.LegDeltaMax,
	hasSpec: delta.LegDeltaMax !== null,
});

const toRow = (
	inspection: TestShackReportInspection,
	columns: TestShackReportColumn[],
): TestShackReportSectionRevRow => {
	const cellsByColumn = new Map<string, TestShackReportCell>();
	for (const dimension of inspection.items) {
		cellsByColumn.set(dimensionColumnKey(dimension.ProfileItemId), {
			value: dimension.NumericValue,
			status: dimension.Status,
		});
	}
	for (const delta of inspection.legDeltas) {
		cellsByColumn.set(deltaColumnKey(delta.DeltaCode), {
			value: delta.Value,
			status: delta.Status,
		});
	}

	return {
		inspection,
		cells: columns.map((column) => cellsByColumn.get(column.key) ?? null),
		outOfSpecCount: inspection.OutOfSpecCount,
		hasTolerances: hasInspectionTolerances(inspection),
	};
};

const toSectionRev = (
	inspections: TestShackReportInspection[],
): TestShackReportSectionRev => {
	const [head] = inspections;
	const dimensions = new Map<number, TestShackReportItem>();
	const deltas = new Map<string, TestShackReportLegDelta>();
	for (const inspection of inspections) {
		for (const dimension of inspection.items) {
			const known = dimensions.get(dimension.ProfileItemId);
			if (
				known === undefined ||
				(known.Ltl === null &&
					known.Utl === null &&
					(dimension.Ltl !== null || dimension.Utl !== null))
			)
				dimensions.set(dimension.ProfileItemId, dimension);
		}
		for (const delta of inspection.legDeltas) {
			const known = deltas.get(delta.DeltaCode);
			if (
				known === undefined ||
				(known.LegDeltaMax === null && delta.LegDeltaMax !== null)
			)
				deltas.set(delta.DeltaCode, delta);
		}
	}

	const columns: TestShackReportColumn[] = [
		...[...dimensions.values()]
			.toSorted(
				(left, right) =>
					left.SortOrder - right.SortOrder ||
					left.Code.localeCompare(right.Code),
			)
			.map(toDimensionColumn),
		...[...deltas.values()]
			.toSorted((left, right) => left.SortOrder - right.SortOrder)
			.map(toDeltaColumn),
	];
	const rows = inspections.map((inspection) => toRow(inspection, columns));
	const productionTimes = inspections.map((inspection) =>
		inspection.ProductionDate.getTime(),
	);

	return {
		key: String(head.ProfileSpecId),
		profileSpecId: head.ProfileSpecId,
		sectionId: head.SectionId,
		sectionName: head.SectionName,
		specRev: head.SpecRev,
		specSubRev: head.SpecSubRev,
		dimensionsReferenceUploadFileId:
			inspections.find(
				(inspection) => inspection.DimensionsReferenceUploadFileId !== null,
			)?.DimensionsReferenceUploadFileId ?? null,
		millDrawingUploadFileId:
			inspections.find(
				(inspection) => inspection.MillDrawingUploadFileId !== null,
			)?.MillDrawingUploadFileId ?? null,
		columns,
		rows,
		outOfSpecCount: rows.reduce((total, row) => total + row.outOfSpecCount, 0),
		hasTolerances: rows.some((row) => row.hasTolerances),
		firstProductionDate: new Date(Math.min(...productionTimes)),
		lastProductionDate: new Date(Math.max(...productionTimes)),
	};
};

export const groupTestShackReportBySectionRev = (
	inspections: TestShackReportInspection[],
): TestShackReportSectionRev[] => {
	const inspectionsBySectionRev = new Map<
		number,
		TestShackReportInspection[]
	>();
	for (const inspection of inspections) {
		const known = inspectionsBySectionRev.get(inspection.ProfileSpecId);
		if (known) known.push(inspection);
		else inspectionsBySectionRev.set(inspection.ProfileSpecId, [inspection]);
	}

	return [...inspectionsBySectionRev.values()].map(toSectionRev);
};
